import React, { useEffect, useState } from "react";
import { Box, Chip, Link, Tab, Tabs, Typography } from "@mui/material";
import { api } from "../../utils/api";
import { DATA_GRID_STYLES } from "../../components/BasicUserInfo";
import AdminResyncFromStripe from "../../components/AdminResyncFromStripe";

function formatDate(value) {
  if (!value) return "n/a";
  return new Date(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function GridRow({ label, value }) {
  return (
    <>
      <span>{label}</span>
      <span style={{ wordBreak: "break-word" }}>{value == null || value === "" ? "n/a" : value}</span>
    </>
  );
}

const SubscriptionStripeCard = ({ subscriptionId, hasStripeId, onResync }) => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!subscriptionId || !hasStripeId) {
      setData(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await api(`v1/subscription/${subscriptionId}/stripe`, "GET");
        if (!cancelled) {
          if (response.data.success) {
            setData(response.data.data || null);
          } else {
            setError(response.data.message || "Could not load Stripe details");
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err.message || "Could not load Stripe details");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [subscriptionId, hasStripeId]);

  if (!hasStripeId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <Typography color="text.secondary">This membership is not linked to a Stripe subscription.</Typography>
      </div>
    );
  }

  const sub = data && data.subscription;
  const customer = data && data.customer;
  const pm = data && data.paymentMethod;
  const invoices = (data && data.invoices) || [];
  const dashBase = data && data.livemode ? "https://dashboard.stripe.com" : "https://dashboard.stripe.com/test";

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden w-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Stripe</h2>
          {sub && (
            <Typography variant="body2" color="text.secondary">
              {sub.id} · {data.livemode ? "Live" : "Test"}
            </Typography>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {sub && (
            <Chip size="small" label={sub.status || "unknown"} color={sub.status === "active" ? "success" : "default"} />
          )}
          {sub && (
            <Link href={`${dashBase}/subscriptions/${sub.id}`} target="_blank" rel="noreferrer">
              Open in Stripe
            </Link>
          )}
          <AdminResyncFromStripe
            subscriptionId={subscriptionId}
            hasStripeId={hasStripeId}
            onSuccess={onResync}
            compact
          />
        </div>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "white" }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Subscription" />
          <Tab label="Customer" />
          <Tab label="Payment method" />
          <Tab label={`Invoices (${invoices.length})`} />
        </Tabs>
      </Box>

      <div className="p-4">
        {loading && <Typography color="text.secondary">Loading Stripe details…</Typography>}
        {error && <Typography color="error">{error}</Typography>}

        {!loading && !error && tab === 0 && sub && (
          <div className={DATA_GRID_STYLES}>
            <GridRow label="Subscription ID" value={sub.id} />
            <GridRow label="Status" value={sub.status} />
            <GridRow label="Collection" value={sub.collectionMethod} />
            <GridRow label="Created" value={formatDate(sub.created)} />
            <GridRow label="Start date" value={formatDate(sub.startDate)} />
            <GridRow label="Period start" value={formatDate(sub.currentPeriodStart)} />
            <GridRow label="Period end" value={formatDate(sub.currentPeriodEnd)} />
            <GridRow label="Trial end" value={formatDate(sub.trialEnd)} />
            <GridRow label="Cancel at period end" value={sub.cancelAtPeriodEnd ? "Yes" : "No"} />
            <GridRow label="Canceled at" value={formatDate(sub.canceledAt)} />
            {(sub.items || []).map((item, idx) => (
              <React.Fragment key={item.id || idx}>
                <GridRow
                  label={`Price ${idx + 1}`}
                  value={`${item.nickname || item.priceId || "n/a"} · ${item.amount?.formatted || "n/a"} / ${item.interval || "n/a"}`}
                />
              </React.Fragment>
            ))}
            {sub.latestInvoice && (
              <GridRow
                label="Latest invoice"
                value={
                  sub.latestInvoice.hostedInvoiceUrl ? (
                    <Link href={sub.latestInvoice.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                      {sub.latestInvoice.id} ({sub.latestInvoice.status})
                    </Link>
                  ) : (
                    `${sub.latestInvoice.id || "n/a"} (${sub.latestInvoice.status || "n/a"})`
                  )
                }
              />
            )}
          </div>
        )}

        {!loading && !error && tab === 1 && (
          <div className={DATA_GRID_STYLES}>
            <GridRow label="Customer ID" value={customer && customer.id} />
            <GridRow label="Name" value={customer && customer.name} />
            <GridRow label="Email" value={customer && customer.email} />
            <GridRow label="Phone" value={customer && customer.phone} />
            <GridRow label="Delinquent" value={customer && customer.delinquent ? "Yes" : "No"} />
            <GridRow
              label="Balance"
              value={
                customer && customer.balance != null
                  ? (Number(customer.balance) / 100).toLocaleString(undefined, {
                      style: "currency",
                      currency: "AUD",
                    })
                  : "n/a"
              }
            />
            <GridRow label="Created" value={formatDate(customer && customer.created)} />
            {customer && customer.id && (
              <GridRow
                label="Dashboard"
                value={
                  <Link href={`${dashBase}/customers/${customer.id}`} target="_blank" rel="noreferrer">
                    Open customer
                  </Link>
                }
              />
            )}
          </div>
        )}

        {!loading && !error && tab === 2 && (
          <div className={DATA_GRID_STYLES}>
            <GridRow label="Payment method" value={pm && pm.id} />
            <GridRow label="Type" value={pm && pm.type} />
            <GridRow label="Brand" value={pm && pm.brand} />
            <GridRow label="Last 4" value={pm && pm.last4} />
            <GridRow
              label="Expiry"
              value={pm && pm.expMonth ? `${pm.expMonth}/${pm.expYear}` : "n/a"}
            />
            <GridRow label="Name on card" value={pm && pm.billingName} />
          </div>
        )}

        {!loading && !error && tab === 3 && (
          <div className="overflow-x-auto">
            {invoices.length === 0 ? (
              <Typography color="text.secondary">No invoices on this Stripe subscription.</Typography>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Invoice</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Reason</th>
                    <th className="py-2 pr-3">Paid</th>
                    <th className="py-2">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-gray-100">
                      <td className="py-2 pr-3">{formatDate(inv.created)}</td>
                      <td className="py-2 pr-3">{inv.number || inv.id}</td>
                      <td className="py-2 pr-3">{inv.status}</td>
                      <td className="py-2 pr-3">{inv.billingReason || "n/a"}</td>
                      <td className="py-2 pr-3">{inv.amountPaid?.formatted || "n/a"}</td>
                      <td className="py-2">
                        {inv.hostedInvoiceUrl ? (
                          <Link href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                            View
                          </Link>
                        ) : (
                          "n/a"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionStripeCard;
